var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect } from "react";
const ScriptSnippet = ({ data, target = "body", preserveOrder = true, dedupeBySrc = true, cleanup, onLoadAll, onError, }) => {
    useEffect(() => {
        if (!data.content)
            return;
        // Parse once
        const doc = new DOMParser().parseFromString(data.content, "text/html");
        const scriptNodes = Array.from(doc.querySelectorAll("script"));
        if (scriptNodes.length === 0)
            return;
        const container = target === "head" ? document.head : document.body;
        const appended = [];
        // Utility: clone attributes 1:1
        const cloneAttributes = (from, to) => {
            for (const { name, value } of Array.from(from.attributes)) {
                to.setAttribute(name, value);
            }
        };
        const loadExternal = (el) => new Promise((resolve, reject) => {
            el.addEventListener("load", () => resolve(), { once: true });
            el.addEventListener("error", (e) => reject(e), { once: true });
            container.appendChild(el);
            appended.push(el);
        });
        const insertInline = (srcNode) => {
            var _a;
            const el = document.createElement("script");
            cloneAttributes(srcNode, el);
            // Inline code goes in textContent
            el.textContent = (_a = srcNode.textContent) !== null && _a !== void 0 ? _a : "";
            container.appendChild(el);
            appended.push(el);
        };
        const alreadyHasSrc = (src) => !!document.querySelector(`script[src="${CSS.escape(src)}"]`);
        (() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                // Process in DOM order
                for (const node of scriptNodes) {
                    const hasSrc = !!node.src;
                    const isAsync = node.hasAttribute("async");
                    const isDefer = node.hasAttribute("defer");
                    const type = node.getAttribute("type"); // e.g., "module", "application/javascript", etc.
                    if (hasSrc) {
                        if (dedupeBySrc && alreadyHasSrc(node.src)) {
                            // Skip duplicate
                            continue;
                        }
                        const el = document.createElement("script");
                        cloneAttributes(node, el);
                        el.src = node.src;
                        // If preserving order and the tag is NOT async/defer, await load before continuing.
                        if (preserveOrder && !isAsync && !isDefer) {
                            yield loadExternal(el);
                        }
                        else {
                            // Fire and forget
                            container.appendChild(el);
                            appended.push(el);
                        }
                    }
                    else {
                        // Inline script
                        // For modules, we must set type="module" to preserve semantics.
                        // cloneAttributes handles this already.
                        insertInline(node);
                        // Note: inline <script type="module"> executes as a module when appended.
                        // There is no load event for inline scripts—execution is synchronous at append time.
                    }
                }
                onLoadAll === null || onLoadAll === void 0 ? void 0 : onLoadAll();
            }
            catch (e) {
                onError === null || onError === void 0 ? void 0 : onError(e);
            }
        }))();
        return () => {
            var _a;
            // Remove all inserted scripts
            for (const el of appended) {
                (_a = el.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(el);
            }
            // Allow caller to clean up globals/iframes/etc. created by the scripts.
            cleanup === null || cleanup === void 0 ? void 0 : cleanup();
        };
    }, [
        data.content,
        target,
        preserveOrder,
        dedupeBySrc,
        cleanup,
        onLoadAll,
        onError,
    ]);
    return null;
};
export default ScriptSnippet;
